import { createTheme } from "@material-ui/core/styles"
import { NudgeDomain } from "./constants"

export const colors = {
  neutral: {
    white: "#FFFFFF",
    clay: "#C8CDDB",
    cement: "#909CBC",
    asphalt: "#545E78",
    denim: "#2F3B5A",
    denim_08: "rgba(47, 59, 90, 0.8)",
    space: "#1E263C",
    space_08: "rgba(30,38,60,0.8)",
    surface_subtle: "#EDEEEF",
    ink: "#0B1950",
  },
  opacities: {
    black03: "rgba(30,38,60,0.03)",
  },
  primary: {
    cloud: "#EEF5FD",
    cloud_05: "rgba(238, 245, 253, 0.5)",
    mist: "#DBE5F8",
    ice: "#C1CEF3",
    ice_03: "rgba(193,206,243,0.3)",
    periwinkle: "#7591E4",
    shadow: "#4F66AA",
    midnight: "#3D4F83",
  },
  secondary: {
    blush: "#FFDBD9",
    peach: "#FFC4BD",
    rose: "#FF9D94",
    grapefruit: "#FF776C",
    grapefruit_03: "rgba(255,119,108,0.3)",
    plum: "rgba(137,98,208, 0.2)",
    raspberry: "#FF5858",
    crimson: "#E23E4F",
  },
  tertiary: {
    tangerine: "#FFAC62",
    violet: "#8962D0",
    orange: "#FF806C",
  },
  dbt: {
    intensity: {
      0: "#F5F5F5",
      1: "#DFD0FA",
      2: "#B9A1E3",
      3: "#9676CE",
      4: "#7E58C0",
      5: "#6438B1",
      6: "#7930A2",
      7: "#AA2886",
    },
    meals: {
      possibleConcern: "#942BB0",
      somewhatLow: "#7A3ABE",
    },
    text: {
      neutral: "#474747",
      lowIntensity: "#421393",
      normalMeal: "#3E0F90",
    },
    borders: {
      lowIntensity: "#D2C0F3",
      mildIntensity: "#B096DE",
      moderateIntensity: "#916FCB",
      normalMeal: "#6135af1a",
    },
  },
  graphs: {
    cravings: {
      intensity500: "#9572d5",
      intensity400: "#9572d5",
      intensity300: "#9e7ed8",
      intensity200: "#ac91de",
      intensity100: "#b8a1e3",
    },
    enjoyment: {
      intensity100: "color-mix(in oklab, #7591E4 82%, white)",
      intensity200: "color-mix(in oklab, #7591E4 90%, white)",
      intensity300: "#7591E4",
    },
    alerts: {
      positive: "#007546",
      negative: "#D33444",
    },
    myPlaces: {
      safePlace: "#33C189",
      riskyPlace: "#D33444",
    },
    labels: {
      colorText: "#737373",
    },
  },
  trendingData: {
    orange: {
      light: "#FFE4CC",
      dark: "#FFAC62",
    },
    red: {
      light: "#FFB2B2",
      dark: "#FF5858",
    },
    blue: {
      light: "#CCF6FF",
      dark: "#0BBBE2",
    },
    green: {
      light: "#E0FFE5",
      dark: "#92DB9D",
    },
  },
  questionnaires: {
    green: {
      light: "#8CDAB5",
      dark: "#19B66C",
    },
    yellow: {
      light: "#FFE895",
      dark: "#FDCD25",
    },
    orange: {
      light: "#FFBD80",
      dark: "#FF7A00",
    },
    red: {
      light: "#FF8383",
      dark: "#FF0707",
    },
    gray: {
      light: "#a9a9a9",
      dark: "#808080",
    },
  },
  safetyPlan: {
    grayscale: {
      black10: "rgba(0, 0, 0, 0.10)",
      white75: "rgba(255, 255, 255, 0.75)",
      white85: "rgba(255, 255, 255, 0.85)",
    },
    graphText: "#737373",
    cardSecondaryBackground: "#FCFCFC",
    background: "rgba(0, 0, 0, 0.03)",
    mediumBlue: "rgba(66, 79, 128, 0.70)",
    ink: "#0B1950",
    midnight: "#424F80",
    ash: " #E9EBF2",
    sky: "#0BBBE2",
    lime: "#33C189",
    ice: "#BAC8F1",
    cloud: "#EEF2FF",
    danger: "#D33444",
    dangerTint: "rgba(211, 52, 68, 0.03)",
  },
  schedule: {
    [NudgeDomain.MOTIVATIONAL_INTERVIEWING]: {
      border: "#7591E4",
      background: "rgba(117, 145, 228, 0.2)",
    },
    [NudgeDomain.BEHAVIORAL_ACTIVATION]: {
      border: "#8962D0",
      background: "rgba(137, 98, 208, 0.2)",
    },
    [NudgeDomain.COGNITIVE_RESTRUCTURING]: {
      border: "#0BBBE2",
      background: "rgba(11, 187, 226, 0.2)",
    },
    [NudgeDomain.MINDFULNESS_AND_RELAXATION]: {
      border: "#FFAC62",
      background: "rgba(255, 172, 98, 0.2)",
    },
    [NudgeDomain.EXPOSURE_THERAPY]: {
      border: "#92DB9D",
      background: "rgba(146, 219, 157, 0.2)",
    },
    [NudgeDomain.SLEEP_IMPROVEMENT]: {
      border: "#3D4F83",
      background: "rgba(61, 79, 131, 0.2)",
    },
    [NudgeDomain.ASSERTIVENESS_TRAINING]: {
      border: "#E23E4F",
      background: "rgba(226, 62, 79, 0.2)",
    },
    [NudgeDomain.RELAPSE_PREVENTION]: {
      border: "#14D0BA",
      background: "rgba(20, 208, 186, 0.2)",
    },
    [NudgeDomain.PRACTITIONER_DEFINED]: {
      border: "#FF806C",
      background: "rgba(255, 128, 108, 0.2)",
    },
    [NudgeDomain.EMOTIONAL_COOLDOWN]: {
      border: "#9999ff",
      background: "#9999ff",
    },
    [NudgeDomain.DBT_MINDFULNESS]: {
      border: "#D014BE",
      background: "rgba(208, 20, 190, 0.2)",
    },
    [NudgeDomain.DBT_DISTRESS_TOLERANCE]: {
      border: "#D014BE",
      background: "rgba(208, 20, 190, 0.2)",
    },
    [NudgeDomain.DBT_EMOTION_REGULATION]: {
      border: "#D014BE",
      background: "rgba(208, 20, 190, 0.2)",
    },
    [NudgeDomain.DBT_INTERPERSONAL_EFFECTIVENESS]: {
      border: "#D014BE",
      background: "rgba(208, 20, 190, 0.2)",
    },
    [NudgeDomain.DBT_WALKING_THE_MIDDLE_PATH]: {
      border: "#D014BE",
      background: "rgba(208, 20, 190, 0.2)",
    },
    [NudgeDomain.KEEP_ME_CALM]: {
      border: "#4C288E",
      background: "#F5F1F9",
    },
    [NudgeDomain.KEEP_ME_SAFE]: {
      border: "#D33444",
      background: "#FEF6F7",
    },
    QUESTIONNAIRES: {
      border: "#4F66AA",
      background: "rgba(193, 206, 243, 1)",
    },
  },
}

export const theme = createTheme({
  spacing: 8,
  palette: {
    primary: {
      main: colors.primary.midnight,
    },
  },
  props: {
    MuiButtonBase: {
      // The properties to apply
      disableRipple: true, // No more ripple, on the whole application!
    },
  },

  overrides: {
    MuiCssBaseline: {
      "@global": {
        html: {
          height: "100%",
        },
        body: {
          height: "100%",
        },
      },
    },
    MuiIconButton: {
      root: {
        "&:hover": {
          backgroundColor: "transparent",
        },
      },
    },
    MuiPickersToolbar: {
      toolbar: {
        backgroundColor: colors.primary.shadow,
      },
    },
    MuiFormLabel: {
      asterisk: { color: "red" },
    },
  },
  typography: {
    fontFamily: ["Lato", "san-serif"].join(","),
    fontSize: 16,
    h1: {
      fontFamily: "Lato",
      fontWeight: 700,
      fontSize: 34,
      letterSpacing: 0.3,
      color: colors.neutral.denim,
    },
    h2: {
      fontFamily: "Lato",
      fontWeight: 700,
      fontSize: 30,
      letterSpacing: 0.3,
      color: colors.neutral.space,
    },
    h3: {
      fontFamily: "Lato",
      fontWeight: 700,
      fontSize: 24,
      letterSpacing: 0.3,
      color: colors.neutral.space,
    },
    h4: {
      fontFamily: "Lato",
      fontWeight: 700,
      fontSize: 20,
      letterSpacing: 0.3,
      color: colors.neutral.space,
    },
    h5: {
      fontFamily: "Lato",
      fontWeight: 700,
      fontSize: 18,
      letterSpacing: 0.3,
      color: colors.neutral.space,
    },
    h6: {
      fontFamily: "Lato",
      fontWeight: 700,
      fontSize: 16,
      letterSpacing: 0.3,
      color: colors.neutral.space,
    },
    subtitle1: {
      fontFamily: "Lato",
      fontWeight: 700,
      fontSize: 20,
      letterSpacing: 0.3,
      color: colors.neutral.space,
    },
    subtitle2: {
      fontFamily: "Lato",
      fontWeight: 700,
      fontSize: 16,
      letterSpacing: 0.3,
      color: colors.neutral.space,
    },
    body1: {
      fontFamily: "Lato",
      fontWeight: 400,
      fontSize: 16,
      letterSpacing: 0.3,
      color: colors.neutral.space,
    },
    body2: {
      fontFamily: "Lato",
      fontWeight: 700,
      fontSize: 14,
      letterSpacing: 0.3,
      color: colors.neutral.space_08,
    },
    button: {
      fontFamily: "Lato",
      fontWeight: 700,
      fontSize: 20,
      letterSpacing: 0.3,
      color: colors.neutral.space,
    },
    caption: {
      fontFamily: "Lato",
      fontWeight: 700,
      fontSize: 14,
      letterSpacing: 0.3,
      color: colors.neutral.space_08,
    },
    overline: {
      fontFamily: "Lato",
      fontWeight: 700,
      fontSize: 12,
      letterSpacing: 0.5,
      color: colors.neutral.space_08,
    },
  },
})
